import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, getDocs, query, orderBy, deleteDoc, doc, getDoc, updateDoc, onSnapshot } from "firebase/firestore";
import { Plus, Search, Filter, Trash2, Download, TrendingUp, TrendingDown, ArrowLeft, Edit2, Calendar, User, FilterX, IndianRupee, Building2, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import { TransactionModal } from './AccountsModals.js';
import { Link, useLocation } from 'react-router-dom';
import Spinner from './Spinner.js';

const SummaryCard = ({ title, value, color, icon }) => (
    <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flex: 1,
        minWidth: '200px',
        borderLeft: `4px solid ${color}`
    }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>{title}</span>
            <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
        </div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>
            ₹{value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
        </div>
    </div>
);

const TransactionLedger = ({ db }) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [firmFilter, setFirmFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [categories, setCategories] = useState([]);
    const [firms, setFirms] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    
    const [showModal, setShowModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);

    const location = useLocation(); // Initialize useLocation
    const rowRefs = useRef({}); // Ref for storing row elements
    const [highlightedRowId, setHighlightedRowId] = useState(null); // State for highlighting

    useEffect(() => {
        setLoading(true);
        const q = query(collection(db, "accounts_transactions"), orderBy("date", "desc"));
        
        const unsubscribeTransactions = onSnapshot(q, (snapshot) => {
            setTransactions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            console.error("Error fetching transactions:", error);
            setLoading(false);
        });

        // Fetch categories
        const catQ = query(collection(db, "accounts_categories"));
        const unsubscribeCategories = onSnapshot(catQ, (snapshot) => {
            setCategories(snapshot.docs.map(doc => doc.data().name));
        });

        // Fetch firms
        getDocs(collection(db, "firms")).then(firmSnap => {
            setFirms(firmSnap.docs.map(doc => ({ id: doc.id, name: doc.data().name })));
        });

        return () => {
            unsubscribeTransactions();
            unsubscribeCategories();
        };
    }, [db]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const highlightId = queryParams.get('highlight');

        if (highlightId && transactions.length > 0) {
            const rowElement = rowRefs.current[highlightId];
            if (rowElement) {
                rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setHighlightedRowId(highlightId);

                // Remove highlight after a few seconds
                const timer = setTimeout(() => {
                    setHighlightedRowId(null);
                    // Clear the highlight param from URL without reloading
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.delete('highlight');
                    window.history.replaceState({}, '', newUrl.toString());
                }, 3000); // Highlight for 3 seconds

                return () => clearTimeout(timer);
            }
        }
    }, [transactions, location.search]);

    const filteredTransactions = transactions.filter(t => {
        const matchesSearch = t.category.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             (t.remark || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (t.entityName || '').toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesType = typeFilter === 'all' || t.type === typeFilter;
        const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
        const matchesFirm = firmFilter === 'all' || t.firm === firmFilter;

        const trxnDate = new Date(t.date);
        trxnDate.setHours(0,0,0,0);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        if (start) start.setHours(0,0,0,0);
        if (end) end.setHours(23,59,59,999);

        const matchesDate = (!start || trxnDate >= start) && (!end || trxnDate <= end);

        return matchesSearch && matchesType && matchesCategory && matchesFirm && matchesDate;
    });

    const hasActiveFilters = searchTerm !== '' || typeFilter !== 'all' || categoryFilter !== 'all' || firmFilter !== 'all' || startDate !== '' || endDate !== '';

    const sortedTransactions = useMemo(() => {
        let sortableItems = [...filteredTransactions];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // Handle string comparison (case-insensitive)
                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();

                // Handle date comparison
                if (sortConfig.key === 'date') {
                    aVal = new Date(aVal).getTime();
                    bVal = new Date(bVal).getTime();
                }

                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredTransactions, sortConfig]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown size={12} style={{ marginLeft: '5px', opacity: 0.5 }} />;
        return sortConfig.direction === 'asc' ? 
            <ChevronUp size={12} style={{ marginLeft: '5px' }} /> : 
            <ChevronDown size={12} style={{ marginLeft: '5px' }} />;
    };

    const clearFilters = () => {
        setSearchTerm('');
        setTypeFilter('all');
        setCategoryFilter('all');
        setFirmFilter('all');
        setStartDate('');
        setEndDate('');
        setSortConfig({ key: 'date', direction: 'desc' }); // Reset sort
    };

    const applyQuickFilter = (days) => {
        const end = new Date();
        const start = new Date();
        
        if (days === 'thisMonth') {
            start.setDate(1);
        } else if (days === 'lastMonth') {
            start.setMonth(start.getMonth() - 1);
            start.setDate(1);
            const lastMonthEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);
            setStartDate(start.toISOString().split('T')[0]);
            setEndDate(lastMonthEnd.toISOString().split('T')[0]);
            return;
        } else {
            start.setDate(start.getDate() - days);
        }
        
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
    };

    const stats = filteredTransactions.reduce((acc, t) => {
        if (t.type === 'revenue') acc.rev += t.amount;
        else acc.exp += t.amount;
        return acc;
    }, { rev: 0, exp: 0 });

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this transaction? This action cannot be undone.")) return;

        try {
            const transactionToDelete = transactions.find(t => t.id === id);

            if (transactionToDelete && transactionToDelete.sourceType === 'invoice' && transactionToDelete.sourceId) {
                // If it's an invoice payment, revert the payment in the invoice
                const invoiceRef = doc(db, "accounts_invoices", transactionToDelete.sourceId);
                const invoiceSnap = await getDoc(invoiceRef);

                if (invoiceSnap.exists()) {
                    const invoiceData = invoiceSnap.data();
                    const paymentAmount = transactionToDelete.amount;

                    const newAmountPaid = (invoiceData.amountPaid || 0) - paymentAmount;
                    const newBalance = invoiceData.totalAmount - newAmountPaid;

                    let newStatus = 'pending';
                    if (newBalance <= 0) {
                        newStatus = 'paid';
                    } else if (newAmountPaid > 0) {
                        newStatus = 'partial';
                    }

                    // Remove the specific payment entry using transactionId
                    const updatedPayments = invoiceData.payments.filter(
                        p => p.transactionId !== id
                    );

                    await updateDoc(invoiceRef, {
                        amountPaid: newAmountPaid,
                        balance: newBalance,
                        status: newStatus,
                        payments: updatedPayments
                    });
                } else {
                    console.warn("Corresponding invoice not found for transaction deletion:", transactionToDelete.sourceId);
                }
            }
            
            await deleteDoc(doc(db, "accounts_transactions", id));
        } catch (e) {
            alert("Error deleting transaction: " + e.message);
            console.error(e);
        }
    };

    if (loading) return <Spinner />;

    return (
        <div style={{ paddingBottom: '40px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <Link to="/accounts" style={{ color: '#64748b' }}><ArrowLeft size={20}/></Link>
                    <h2 style={{ margin: 0 }}>Transaction Ledger</h2>
                </div>
            </div>

            {/* Mini Dashboard */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                <SummaryCard title="Total Revenue" value={stats.rev} color="#10b981" icon={<TrendingUp size={20}/>} />
                <SummaryCard title="Total Expenses" value={stats.exp} color="#ef4444" icon={<TrendingDown size={20}/>} />
                <SummaryCard title="Net Cash Flow" value={stats.rev - stats.exp} color="#2563eb" icon={<IndianRupee size={20}/>} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '10px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {[
                        { label: '15 Days', val: 15 },
                        { label: '30 Days', val: 30 },
                        { label: 'This Month', val: 'thisMonth' },
                        { label: 'Last Month', val: 'lastMonth' }
                    ].map(f => (
                        <button
                            key={f.label}
                            onClick={() => applyQuickFilter(f.val)}
                            style={{
                                padding: '6px 12px', borderRadius: '20px', border: '1px solid #e2e8f0',
                                backgroundColor: 'white', color: '#64748b', fontSize: '12px', cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={e => e.target.style.backgroundColor = '#f8fafc'}
                            onMouseOut={e => e.target.style.backgroundColor = 'white'}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Filters */}
            <div style={{ 
                backgroundColor: 'white', padding: '15px', borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '25px',
                display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'
            }}>
                <div style={{ position: 'relative', flex: '1.5', minWidth: '180px' }}>
                    <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                    <input 
                        placeholder="Search category, remark or entity..." 
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={filterInputStyle}
                    />
                </div>

                <div style={{ position: 'relative', flex: '1', minWidth: '120px' }}>
                    <Building2 style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                    <select 
                        value={firmFilter}
                        onChange={e => setFirmFilter(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '40px' }}
                    >
                        <option value="all">All Firms</option>
                        {firms.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                    </select>
                </div>

                <div style={{ flex: '0.8', minWidth: '110px' }}>
                    <select 
                        value={typeFilter}
                        onChange={e => setTypeFilter(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '12px' }}
                    >
                        <option value="all">All Types</option>
                        <option value="revenue">Revenue</option>
                        <option value="expense">Expense</option>
                    </select>
                </div>

                <div style={{ flex: '1', minWidth: '130px' }}>
                    <select 
                        value={categoryFilter}
                        onChange={e => setCategoryFilter(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '12px' }}
                    >
                        <option value="all">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: '1.2', minWidth: '220px' }}>
                    <Calendar size={18} color="#64748b" />
                    <input 
                        type="date" 
                        value={startDate} 
                        onChange={e => setStartDate(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '10px' }}
                        title="Start Date"
                    />
                    <span style={{ color: '#94a3b8' }}>-</span>
                    <input 
                        type="date" 
                        value={endDate} 
                        onChange={e => setEndDate(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '10px' }}
                        title="End Date"
                    />
                </div>
                
                <button 
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    style={{ 
                        display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 12px',
                        backgroundColor: hasActiveFilters ? '#fee2e2' : '#f1f5f9', 
                        border: hasActiveFilters ? '1px solid #fecaca' : 'none',
                        borderRadius: '8px',
                        color: hasActiveFilters ? '#dc2626' : '#94a3b8', 
                        cursor: hasActiveFilters ? 'pointer' : 'default', 
                        fontSize: '13px', fontWeight: '600',
                        transition: 'all 0.2s',
                        flex: 'none'
                    }}
                >
                    <FilterX size={16} /> Clear
                </button>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <tr>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('date')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Date {getSortIcon('date')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('type')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Type {getSortIcon('type')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('firm')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Firm {getSortIcon('firm')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('entityName')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {typeFilter === 'revenue' ? 'Receivable from' : 
                                     typeFilter === 'expense' ? 'Payable to' : 'Entity Name'}
                                    {getSortIcon('entityName')}
                                </div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('category')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Category {getSortIcon('category')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('remark')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Remark {getSortIcon('remark')}</div>
                            </th>
                            <th style={{ ...thStyle, textAlign: 'right', cursor: 'pointer' }} onClick={() => requestSort('amount')}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>Amount {getSortIcon('amount')}</div>
                            </th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedTransactions.map(t => (
                            <tr 
                                key={t.id} 
                                ref={el => rowRefs.current[t.id] = el}
                                style={{ 
                                    borderBottom: '1px solid #f1f5f9', 
                                    backgroundColor: t.id === highlightedRowId ? '#e6f7ff' : 'white', // Highlight style
                                    transition: 'background-color 0.5s ease'
                                }}
                            >
                                <td style={tdStyle}>{new Date(t.date).toLocaleDateString()}</td>
                                <td style={tdStyle}>
                                    <span style={{ 
                                        padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase',
                                        backgroundColor: t.type === 'revenue' ? '#dcfce7' : '#fee2e2',
                                        color: t.type === 'revenue' ? '#166534' : '#991b1b'
                                    }}>
                                        {t.type}
                                    </span>
                                </td>
                                <td style={{ ...tdStyle, fontSize: '12px', fontWeight: '500', color: '#475569' }}>{t.firm || '—'}</td>
                                <td style={{ ...tdStyle, fontWeight: '600' }}>
                                    {t.sourceType === 'invoice' && t.sourceId ? (
                                        <Link to={`/accounts/invoices?highlight=${t.sourceId}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            {t.entityName || 'N/A'} (Inv: {t.invoiceNo})
                                        </Link>
                                    ) : (
                                        t.entityName || 'N/A'
                                    )}
                                </td>
                                <td style={{ ...tdStyle, fontWeight: '600' }}>{t.category}</td>
                                <td style={{ ...tdStyle, color: '#64748b', fontSize: '12px' }}>{t.remark || '—'}</td>
                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: t.type === 'revenue' ? '#059669' : '#dc2626' }}>
                                    ₹{t.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                        <button 
                                            onClick={() => { setSelectedTransaction(t); setShowModal(true); }}
                                            style={{ 
                                                padding: '6px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', 
                                                backgroundColor: 'white', cursor: 'pointer' 
                                            }}
                                            title="Edit Transaction"
                                        >
                                            <Edit2 size={12} color="#64748b" />
                                        </button>
                                        <button onClick={() => handleDelete(t.id)} 
                                            style={{ 
                                                padding: '6px 8px', borderRadius: '6px', border: '1px solid #fee2e2', 
                                                backgroundColor: 'white', cursor: 'pointer' 
                                            }}
                                            title="Delete Transaction"
                                        >
                                            <Trash2 size={12} color="#ef4444" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredTransactions.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No transactions found matching your filters.</div>
                )}
            </div>

            <TransactionModal 
                show={showModal} 
                onClose={() => { setShowModal(false); setSelectedTransaction(null); }} 
                onSave={() => setShowModal(false)} 
                db={db} 
                transactionData={selectedTransaction}
            />
        </div>
    );
};

const thStyle = { padding: '15px 20px', textAlign: 'left', fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' };
const filterInputStyle = { padding: '10px 12px', paddingLeft: '40px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', backgroundColor: '#fff', fontSize: '14px', width: '100%', boxSizing: 'border-box' };
const tdStyle = { padding: '15px 20px', fontSize: '14px', verticalAlign: 'middle' }; // Added verticalAlign for consistency

export default TransactionLedger;
